<template>
  <ModalShell
    :labelled-by="titleId"
    :dismissable="phase !== 'submitting'"
    @close="emit('close')"
  >
    <template v-if="phase === 'success' && heldRow">
      <h2
        :id="titleId"
        class="ex-title"
      >
        {{ t('exclusiveUnits.dialog.success.title') }}
      </h2>
      <p
        class="ex-success"
        role="status"
      >
        {{ successLine }}
      </p>
      <p class="ex-tz">{{ t('exclusiveUnits.dialog.success.timeZone', { city: timeZoneCity(timeZone) }) }}</p>
      <div class="ex-buttons">
        <button
          type="button"
          class="btn primary"
          @click="onContinue"
        >
          {{ t('exclusiveUnits.dialog.success.continue') }}
        </button>
        <button
          type="button"
          class="btn ghost"
          @click="emit('close')"
        >
          {{ t('exclusiveUnits.dialog.success.done') }}
        </button>
      </div>
    </template>

    <form
      v-else
      novalidate
      @submit.prevent="onSubmit"
    >
      <h2
        :id="titleId"
        class="ex-title"
      >
        {{ t('exclusiveUnits.dialog.title', { unit: unit.unitNumber }) }}
      </h2>
      <dl class="ex-facts">
        <div>
          <dt>{{ t('exclusiveUnits.dialog.project') }}</dt>
          <dd>{{ projectName }}</dd>
        </div>
        <div>
          <dt>{{ t('exclusiveUnits.dialog.unit') }}</dt>
          <dd>{{ unitLabel }}</dd>
        </div>
        <div v-if="unit.priceAed !== null">
          <dt>{{ t('exclusiveUnits.dialog.price') }}</dt>
          <dd>{{ formatAed(unit.priceAed) }}</dd>
        </div>
      </dl>
      <p
        v-if="earningsText"
        class="ex-earn"
      >
        {{ earningsText }}
      </p>
      <p
        v-if="holdHours"
        class="ex-length"
      >
        {{ t('exclusiveUnits.dialog.holdLength', { hours: holdHours }, holdHours) }}
      </p>

      <ExclusiveHoldClientStep
        v-model:client="client"
        :disabled="phase === 'submitting'"
      />

      <p
        v-if="errorText"
        class="ex-error"
        role="alert"
      >
        {{ errorText }}
      </p>

      <div class="ex-buttons">
        <button
          v-if="!blocked"
          type="submit"
          class="btn primary"
          :disabled="!canSubmit"
        >
          {{ phase === 'submitting' ? t('exclusiveUnits.dialog.submitting') : t('exclusiveUnits.dialog.submit') }}
        </button>
        <button
          type="button"
          class="btn ghost"
          :disabled="phase === 'submitting'"
          @click="emit('close')"
        >
          {{ blocked ? t('exclusiveUnits.dialog.close') : t('exclusiveUnits.dialog.cancel') }}
        </button>
      </div>
    </form>
  </ModalShell>
</template>

<script setup lang="ts">
import ModalShell from '~/components/property/access/modals/ModalShell.vue';
import { useExclusiveHoldActions } from '~/composables/useExclusiveHoldActions';
import type { ExclusiveUnitRow } from '~/types/exclusives';
import { useAedAmount } from '~/composables/useAedAmount';
import { useHoldExclusiveUnit } from '~/api/mutations/exclusives/useHoldExclusiveUnit';
import {
  canSubmitHold,
  formatHoldTime,
  holdErrorCopy,
  holdRequestBody,
  isHeldStatus,
  newIdempotencyKey,
  resolveHoldTimeZone,
  timeZoneCity,
  type HoldClientChoice,
  type HoldDialogPhase,
  type HoldErrorCopy,
} from '~/utils/exclusives/holdDialog';
import { holdRowFromDto, type ExclusiveHoldRow } from '~/utils/exclusives/holdRows';
import ExclusiveHoldClientStep from './ExclusiveHoldClientStep.vue';

const {
  unit,
  catalogueProjectId,
  projectName,
  holdHours = null,
  hideEarnings = false,
} = defineProps<{
  unit: ExclusiveUnitRow;
  catalogueProjectId: number;
  projectName: string;
  holdHours?: number | null;
  hideEarnings?: boolean;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'held', row: ExclusiveHoldRow): void;
}>();

const { t, locale } = useI18n();
const titleId = useId();
const authStore = useAuthStore();
const { formatAed } = useAedAmount();
const { continueToDeal } = useExclusiveHoldActions();
const { mutateAsync: holdUnit } = useHoldExclusiveUnit();

const phase = ref<HoldDialogPhase>('editing');
const client = ref<HoldClientChoice | null>(null);
const error = ref<HoldErrorCopy | null>(null);
const heldRow = ref<ExclusiveHoldRow | null>(null);
// One key per dialog open: a double submit returns the same hold; renewed only after an answered attempt.
let idempotencyKey = newIdempotencyKey();

const blocked = computed(() => !!error.value && !error.value.retryable);
const canSubmit = computed(() => canSubmitHold({ phase: phase.value, client: client.value, blocked: blocked.value }));

const unitLabel = computed(() => [unit.unitNumber, unit.unitType, unit.bedrooms].filter(Boolean).join(' · '));

const earningsText = computed(() => {
  const amount = unit.agentEarningsAed;
  if (hideEarnings || typeof amount !== 'number') return null;
  return t('exclusiveUnits.dialog.youEarn', { amount: formatAed(amount) });
});

const errorText = computed(() => {
  const e = error.value;
  if (!e) return null;
  if (!e.key) return e.serverMessage;
  const key = `exclusiveUnits.dialog.errors.${e.key}`;
  return e.params.max !== undefined ? t(key, e.params, e.params.max) : t(key, e.params);
});

const browserTimeZone = (() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return null;
  }
})();
const timeZone = computed(() => resolveHoldTimeZone(authStore.user?.timezone, browserTimeZone));

const successLine = computed(() => {
  const row = heldRow.value;
  if (!row) return '';
  return t('exclusiveUnits.dialog.success.line', {
    unit: row.unitNumber,
    client: row.clientName,
    time: formatHoldTime(row.expiresAt, timeZone.value, locale.value),
  });
});

const failWith = (copy: HoldErrorCopy) => {
  error.value = copy;
  if (copy.answered) idempotencyKey = newIdempotencyKey();
  phase.value = 'editing';
};

const onSubmit = async () => {
  if (!canSubmit.value || !client.value) return;
  phase.value = 'submitting';
  error.value = null;
  try {
    const { hold } = await holdUnit({
      catalogueProjectId,
      catalogueUnitId: unit.catalogueUnitId,
      body: holdRequestBody(client.value, idempotencyKey),
    });
    const row = isHeldStatus(hold.status) ? holdRowFromDto(hold) : null;
    if (!row) {
      failWith({ key: 'notConfirmed', params: {}, serverMessage: null, retryable: true, answered: true });
      return;
    }
    heldRow.value = row;
    phase.value = 'success';
    emit('held', row);
  } catch (err) {
    failWith(holdErrorCopy(err));
  }
};

const onContinue = () => {
  if (heldRow.value) void continueToDeal(heldRow.value);
};
</script>

<style scoped lang="scss">
.ex-title {
  font-family: 'Fraunces', 'Times New Roman', serif;
  font-size: 24px;
  font-weight: 600;
  line-height: 1.15;
  letter-spacing: -0.015em;
  color: #0d0f15;
  margin: 0 0 14px;
  padding-inline-end: 24px;
}

.ex-facts {
  display: grid;
  gap: 6px;
  margin: 0 0 10px;

  div {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    font-size: 14px;
  }

  dt {
    color: rgba(13, 15, 21, 0.62);
  }

  dd {
    margin: 0;
    font-weight: 600;
    color: #0d0f15;
    text-align: end;
    font-variant-numeric: tabular-nums;
  }
}

.ex-earn {
  font-size: 14px;
  font-weight: 700;
  color: #0d0f15;
  margin: 0 0 6px;
}

.ex-length {
  font-size: 13px;
  color: rgba(13, 15, 21, 0.7);
  margin: 0 0 18px;
}

.ex-error {
  font-size: 13px;
  font-weight: 500;
  color: #b42318;
  margin: 14px 0 0;
}

.ex-success {
  font-size: 15px;
  color: #0d0f15;
  margin: 0 0 6px;
}

.ex-tz {
  font-size: 12px;
  color: rgba(13, 15, 21, 0.62);
  margin: 0 0 18px;
}

.ex-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 18px;
}

.btn {
  display: inline-flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  height: 44px;
  padding: 0 18px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;

  &.primary {
    background: #0d0f15;
    color: #fff;
    border: none;
  }

  &.ghost {
    background: #fff;
    color: #0d0f15;
    border: 1px solid rgba(13, 15, 21, 0.15);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
}
</style>
